import { useState } from 'react'
import schoolOriginalBg from '../assets/school-original-thumbnail-bg.jpg'

const STORAGE_OBJECT_PATH = '/storage/v1/object/public/'

// Thumbnails uploaded to Supabase Storage are full 1280px images (~200KB each), but cards
// show them about 224px wide. Storage can resize on the fly through /render/image/, which
// returns a cacheable copy around a tenth of the size. resize=contain keeps the aspect
// ratio; the default (cover) keeps the original height and crops to a tall 480x720.
// Other URLs (Vimeo, YouTube) are already sized by their CDN and are left alone.
const toResizedUrl = (url: string, width: number): string =>
  url.includes(STORAGE_OBJECT_PATH)
    ? url.replace(STORAGE_OBJECT_PATH, '/storage/v1/render/image/public/') + `${url.includes('?') ? '&' : '?'}width=${width}&resize=contain&quality=75`
    : url

interface VideoThumbnailProps {
  thumbnailUrl: string | null | undefined
  title: string
  className?: string
  /** School みらいワーク content: render generated thumbnail over the 学校オリジナル background when no image */
  schoolOriginal?: boolean
  /** Overlay text (line breaks preserved). Falls back to `title` when empty. */
  thumbnailTitle?: string | null
  /** Width to request from Storage's image resizer (2x the card width); null keeps the original. */
  resizeWidth?: number | null
}

const VideoThumbnail =({ thumbnailUrl, title, className = 'w-full aspect-video', schoolOriginal, thumbnailTitle, resizeWidth = 480 }: VideoThumbnailProps) => {
  // A project without image transformation (dev, on the free plan) answers the resize
  // URL with 403; fall back to the original for that image.
  const [resizeFailedFor, setResizeFailedFor] = useState<string | null>(null)

  if (thumbnailUrl) {
    const src = resizeWidth && resizeFailedFor !== thumbnailUrl ? toResizedUrl(thumbnailUrl, resizeWidth) : thumbnailUrl
    return (
      <img
        src={src}
        alt={title}
        loading="lazy"
        decoding="async"
        onError={() => { if (src !== thumbnailUrl) setResizeFailedFor(thumbnailUrl) }}
        className={`${className} object-cover`}
      />
    )
  }

  if (schoolOriginal) {
    const overlayText = (thumbnailTitle?.trim() || title)
    const lines = overlayText.split('\n')
    return (
      <div
        className={`${className} bg-cover bg-center flex items-center justify-center px-[8%]`}
        style={{ backgroundImage: `url(${schoolOriginalBg})`, containerType: 'inline-size' }}
      >
        <div className="text-center text-black font-bold leading-tight" style={{ fontSize: '11cqw' }}>
          {lines.map((line, i) => (
            <span key={i} className="block">{line}</span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={`${className} bg-gray-100 flex items-center justify-center p-2`}>
      <span className="text-gray-500 text-xs text-center leading-tight line-clamp-3">{title}</span>
    </div>
  )
}

export default VideoThumbnail
